<script setup lang="ts">
  import { ref, reactive, watch, onMounted, onBeforeUnmount, nextTick, computed, h, provide } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, Drawer, Modal, message, Spin } from 'ant-design-vue'
  import {
    PlusOutlined,
    CodeOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    FileSearchOutlined
  } from '@ant-design/icons-vue'
  import { AnalysisConfig } from './models/AnalysisConfig'
  import { AnalysisConfigSchema } from './models/AnalysisConfigSchema'
  import AnalysisConfigSelectionModal from './components/AnalysisConfigSelectionModal.vue'
  import { Calculator, ChevronRight, CloudCog, Download, Loader2, Trash2 } from 'lucide-vue-next'
  import AnalysisConfigConfiguration from './components/AnalysisConfigConfiguration.vue'
  import { AnalysisConfigService } from './services/AnalysisConfigService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { useUser } from '@/composables/useUser'
  import AnalysisConfigJsonManagement from './components/AnalysisConfigJsonManagement.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { AnalysisConfigJsonParsingService } from './services/AnalysisConfigJsonParsingService'
  import JsonAnalysisConfigConfiguration from './components/JsonAnalysisConfigConfiguration.vue'
  import { AnalysisConfigJsonConfigurationService } from './services/AnalysisConfigJsonConfigurationService'
  import { AnalysisConfigTypeGuardService } from './services/AnalysisConfigTypeGuardService'
  import { AnalysisConfigSearchService } from './services/AnalysisConfigSearchService'
  import {
    AnalysisConfigApiService,
    IAnalysisConfigsApiService
  } from './services/AnalysisConfigApiService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { AnalysisConfigsUiService } from './services/AnalysisConfigsUiService'
  import { AnalysisConfigSchemaUiFieldGroup } from './models/AnalysisConfigSchemaUi'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import FileManager from '../FileManager/components/FileManager.vue'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { AnalysisConfigurationFactory } from '../AnalysisWizard/services/AnalysisConfigurationFactory'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { toast } from 'vue-sonner'
  import { AnalysisWizardStepType } from '../AnalysisWizard/models/AnyalsisWizardStepType'
  import { useRouter } from 'vue-router'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'

  interface IAnalysisConfigProps {
    analysisFile?: AplosFile
    analysisConfig?: AnalysisConfig // flattened
    analysisExecution?: Execution // stores last known queued execution for this step
    mode?: 'multiconfig' | 'singleconfig'
    showJsonTab?: boolean
    wizardModel?: AnalysisWizardModel
    showLoading?: boolean
  }

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  const analysisConfigsApiService: IAnalysisConfigsApiService = InjectionService.injectStrict(
    AnalysisConfigApiService.injectableKey
  )

  const analysisConfigJsonParsingService: AnalysisConfigJsonParsingService =
    InjectionService.injectStrict(AnalysisConfigJsonParsingService.injectableKey)

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const router = useRouter()

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = withDefaults(defineProps<IAnalysisConfigProps>(), {
    mode: 'singleconfig'
  })

  const emit = defineEmits<{
    (e: 'update:analysisConfig', value?: AnalysisConfig): void
    (e: 'update:analysisExecution', value?: Execution): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const execution = ref<Execution | undefined>(props.analysisExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  watch(
    () => props.analysisExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  const auth = useUser()

  const isDownloadingResults = ref<boolean>(false)
  const isLoadingAplosFile = ref<boolean>(false)
  const isLoadingAvailableAnalysisConfigs = ref<boolean>(false)
  const hasErrorLoadingAnalysisConfigs = ref<boolean>(false)
  const availableAnalysisConfigSchemas = ref<AnalysisConfigSchema[]>([])
  const activeAnalysisConfigs = reactive<AnalysisConfig[]>([])
  const selectedAnalysisConfig = ref<AnalysisConfig | null>(null)
  const showAnalysisConfigSelection = ref(false)
  const showJsonManagement = ref(false)
  const showFileManager = ref(false)

  const selectedFile = ref<AplosFile | null>(null)

  const availableColumns = ref<Array<{ value: string; label: string }>>([])

  // Helper to load columns from file payload
  const loadAvailableColumns = (analysisFile?: AplosFile) => {
    availableColumns.value = []
    if (!selectedFile.value?.id) return

    let columnNames = analysisFile?.metadata?.columnNames

    if (!columnNames?.length) {
      let filePayload: AplosFile | null = null
      try {
        filePayload = (selectedFile as any).lastPayload || null
      } catch {}
      if (
        filePayload &&
        Array.isArray(filePayload?.metadata?.columnNames) &&
        filePayload?.metadata?.columnNames?.length > 0
      ) {
        columnNames = filePayload.metadata.columnNames
      }
    }

    if (columnNames?.length) {
      // sort alphanumerically (natural numeric-aware ordering) then map
      const sorted = [...columnNames].sort((a, b) =>
        String(a)
          .trim()
          .localeCompare(String(b).trim(), undefined, { numeric: true, sensitivity: 'base' })
      )
      availableColumns.value = sorted.map((name) => ({ value: name, label: name }))
    }
  }

  const isProcessingAnalysisConfigs = ref(false)
  const draggedAnalysisConfig = ref<AnalysisConfig | null>(null)
  const highlightedAnalysisConfigId = ref<string | null>(null)
  const deletingAnalysisConfigId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeAnalysisConfigTabKey = ref<string>('view')
  const activeAnalysisConfigViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeAnalysisConfigEditConfiguration = ref<{} | null>(null)

  watch(
    selectedAnalysisConfig,
    (newVal) => {
      if (newVal) {
        const flattened = AnalysisConfigService.getFlattendAnalysisConfigs([newVal])
        activeAnalysisConfigEditConfiguration.value = flattened?.length ? flattened[0] : null
      }
    },
    { deep: false }
  )

  const onConfigViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedAnalysisConfig.value == null || activeAnalysisConfigEditConfiguration.value == null)
      return
    if (activeMode === 'edit') {
      const flattened = AnalysisConfigService.getFlattendAnalysisConfigs([
        selectedAnalysisConfig.value
      ])
      activeAnalysisConfigEditConfiguration.value = flattened?.length ? flattened[0] : null
    }
  }

  const loadAvailableAnalysisConfigSchemas = async () => {
    hasErrorLoadingAnalysisConfigs.value = false
    isLoadingAvailableAnalysisConfigs.value = true
    try {
      const analysisConfigSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.AnalysisConfigSchema,
        customSchemaStore
      )

      availableAnalysisConfigSchemas.value = analysisConfigSchemas
    } catch (error) {
      hasErrorLoadingAnalysisConfigs.value = true
      message.error('Failed to load available analysis configurations.')
    } finally {
      isLoadingAvailableAnalysisConfigs.value = false
    }
  }

  // --- Add a new config from schema ---
  const addAnalysisConfig = (analysisConfigSchema: AnalysisConfigSchema): AnalysisConfig => {
    const newAnalysisConfig =
      AnalysisConfigService.createAnalysisConfigFromSchema(analysisConfigSchema)

    // In the wizard we use the following factory to create new analyses. This is because properties that are
    // not rendered in the UI don't get populated in our model.
    const defaultAnalysisConfig = AnalysisConfigurationFactory.createNewAnalysisConfiguration()
    Object.assign(newAnalysisConfig, defaultAnalysisConfig)

    activeAnalysisConfigs.push(newAnalysisConfig)
    selectedAnalysisConfig.value = newAnalysisConfig
    updateSharedModelOnAnalysisConfigChange()
    message.success(`Added analysis config: ${analysisConfigSchema.display_name}`)
    return newAnalysisConfig
  }

  const selectAnalysisConfig = (cfg: AnalysisConfig) => {
    selectedAnalysisConfig.value = cfg
  }

  const removeAnalysisConfigTimeoutMs: number = 600
  const removeAnalysisConfig = (cfg: AnalysisConfig) => {
    animateAnalysisConfigDeletion(cfg.id as string)
    message.success(`Removed "${cfg.display_name}"`)
  }

  const animateAnalysisConfigDeletion = (analysisConfigId: string) => {
    deletingAnalysisConfigId.value = analysisConfigId
    setTimeout(() => {
      const idx = activeAnalysisConfigs.findIndex((p) => p.id === analysisConfigId)
      if (idx > -1) activeAnalysisConfigs.splice(idx, 1)
      if (selectedAnalysisConfig.value?.id === analysisConfigId) {
        if (activeAnalysisConfigs?.length > 0) {
          selectedAnalysisConfig.value = activeAnalysisConfigs[0]
        } else {
          selectedAnalysisConfig.value = null
        }
      }
      deletingAnalysisConfigId.value = null
      updateSharedModelOnAnalysisConfigChange()
    }, removeAnalysisConfigTimeoutMs)
  }

  const notifyAnalysisConfigChangedStream$ = new Subject<void>()
  const batchNotifyAnalysisConfigChangedSubscription = notifyAnalysisConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Handle file selection from FileManager
  //@ts-ignore
  const handleFileSelected = (file: AplosFile, rootFile?: AplosFile) => {
    // file is selected (can be derived/cleaned) root is original source
    if (rootFile?.id && rootFile?.name) {
      selectedFile.value = { id: rootFile.id, name: rootFile.name }
      // Attach payload for column_names if present
      ;(selectedFile as any).lastPayload = rootFile
      showFileManager.value = false
      message.success(`Selected file: ${rootFile.name}`, defaultMessageDurationSeconds)
      // Load columns after selecting file
      loadAvailableColumns(rootFile)
    } else {
      message.error('Invalid file selected', defaultMessageDurationSeconds)
    }
  }

  const clearFileSelection = () => {
    selectedFile.value = null
    Modal.confirm({
      title: 'Confirm',
      content: 'Clear existing analysis configs?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        activeAnalysisConfigs.splice(0, activeAnalysisConfigs.length)
        selectedAnalysisConfig.value = null
      },
      onCancel() {}
    })
  }

  watch(selectedFile, (newFile) => {
    if (newFile?.id) {
      loadAvailableColumns(newFile)
    } else {
      availableColumns.value = []
    }
  })

  const onSubmitAnalysisConfigsForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Re-calculating PK Parameters will reset any Post Processing Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitAnalysisConfigsForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitAnalysisConfigsForGeneration()
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:analysisExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:analysisExecution', ex)
    isProcessingAnalysisConfigs.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered executing your analysis.')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Calculating PK Parameters')
    }
  }

  const submitAnalysisConfigsForGeneration = async () => {
    isProcessingAnalysisConfigs.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const flattenedConfigs =
        AnalysisConfigService.getFlattendAnalysisConfigs(activeAnalysisConfigs)

      const args = AnalysisConfigService.createQueueNCAAnalysisArgs(
        selectedFile.value?.id as string,
        flattenedConfigs
      )

      emit('update:analysisConfig', args.analysisConfiguration)

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Calculating PK Parameters',
        props.wizardModel,
        analysisConfigsApiService.processAnalysisConfigs.bind(analysisConfigsApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processAnalysisResponse = response?.data as QueueExecutionResponse
      if (!processAnalysisResponse?.executionId) throw Error('An unexpected error has occurred!')
      const successMessage = 'Calculating PK Parameters'
      /* processRulesResponse?.message?.length
          //? processRulesResponse.message
          : 'Data cleaning queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processAnalysisResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:analysisExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingAnalysisConfigs.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered calculating pk parameters:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingAnalysisConfigs.value ||
      !execution.value?.id ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const viewData = async (aplosFile: AplosFile | null) => {
    if (!aplosFile?.id) toast.error('Error viewing data for selected file. Missing record or Id!')

    const resolved = router.resolve({
      path: '/app/data-viewer',
      query: { fileId: aplosFile?.id }
    })
    const url = window.location.origin + resolved.href
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const moveConfigUp = (analysisConfig: AnalysisConfig) => {
    const currentIndex = activeAnalysisConfigs.findIndex((p) => p.id === analysisConfig.id)
    if (currentIndex > 0) {
      const temp = activeAnalysisConfigs[currentIndex]
      activeAnalysisConfigs[currentIndex] = activeAnalysisConfigs[currentIndex - 1]
      activeAnalysisConfigs[currentIndex - 1] = temp
      highlightMovedAnalysisConfig(analysisConfig.id as string)
      message.success(`Moved "${analysisConfig.display_name}" up to position ${currentIndex}`)
    }
  }

  const moveConfigDown = (cfg: AnalysisConfig) => {
    const currentIndex = activeAnalysisConfigs.findIndex((p) => p.id === cfg.id)
    if (currentIndex < activeAnalysisConfigs.length - 1) {
      const temp = activeAnalysisConfigs[currentIndex]
      activeAnalysisConfigs[currentIndex] = activeAnalysisConfigs[currentIndex + 1]
      activeAnalysisConfigs[currentIndex + 1] = temp
      highlightMovedAnalysisConfig(cfg.id as string)
      message.success(`Moved "${cfg.display_name}" down to position ${currentIndex + 2}`)
    }
  }

  const highlightMovedAnalysisConfig = (analysisConfigId: string) => {
    highlightedAnalysisConfigId.value = analysisConfigId
    setTimeout(() => {
      highlightedAnalysisConfigId.value = null
    }, 1500)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, analysisConfig: AnalysisConfig) => {
    draggedAnalysisConfig.value = analysisConfig
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', analysisConfig.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    if (draggedAnalysisConfig.value) {
      const draggedIndex = activeAnalysisConfigs.findIndex(
        (p) => p.id === draggedAnalysisConfig.value!.id
      )
      if (draggedIndex !== targetIndex) {
        const [movedCfg] = activeAnalysisConfigs.splice(draggedIndex, 1)
        activeAnalysisConfigs.splice(targetIndex, 0, movedCfg)
        highlightMovedAnalysisConfig(draggedAnalysisConfig.value.id as string)
        message.success(
          `Moved "${draggedAnalysisConfig.value.display_name}" to position ${targetIndex + 1}`
        )
      }
    }
    draggedAnalysisConfig.value = null
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    draggedAnalysisConfig.value = null
    dragOverIndex.value = -1
  }

  // Raw Json Edits are parsed back through initialization
  const handleJsonEditorConfigurationChange = () => {
    if (
      selectedAnalysisConfig.value != null &&
      activeAnalysisConfigEditConfiguration.value != null
    ) {
      const editJson = JSON.stringify(activeAnalysisConfigEditConfiguration.value)
      const result = analysisConfigJsonParsingService.tryParseEntitiesFromJson(
        editJson,
        availableAnalysisConfigSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingIndex = activeAnalysisConfigs.findIndex(
          (x) => x.id === selectedAnalysisConfig.value?.id
        )
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingIndex <= -1
        )
          throw new Error('Error updating configs from Json Editor value!')
        const updated = (result.parsedEntities as Array<AnalysisConfig>)[0]
        const existing = activeAnalysisConfigs[existingIndex]
        Object.assign(existing, { ...updated, id: existing.id })
        selectedAnalysisConfig.value = existing
      }
    }
  }

  const updateSharedModelOnAnalysisConfigChange = () => {
    if (!isExecuting.value) {
      emit('update:analysisExecution', undefined)
    }
  }

  // Handle configuration updates
  const handleConfigurationUpdate = async (
    analysisConfig: AnalysisConfig,
    config: Record<string, any>,
    typeChange?: boolean
  ) => {
    const searchResults = AnalysisConfigSearchService.findAnalysisConfigByIdRecursive(
      activeAnalysisConfigs,
      analysisConfig.id as string
    )
    if (searchResults != null) {
      const topIdx = activeAnalysisConfigs.findIndex(
        (p) => p.id === searchResults.topLevelParent?.id
      )
      if (topIdx > -1) {
        if (typeChange === true) {
          const newSchema = AnalysisConfigService.tryGetAnalysisConfigSchemaByType(
            analysisConfig._type,
            availableAnalysisConfigSchemas.value
          )
          if (newSchema == null)
            throw new Error(`Error getting schema for type: ${analysisConfig._type}!`)
          selectedAnalysisConfig.value = null
          let newAnalysisConfig = AnalysisConfigService.createAnalysisConfigFromSchema(newSchema)
          newAnalysisConfig.description = analysisConfig.configuration?.description
          newAnalysisConfig.configuration = { ...config }
          if (
            searchResults.topLevelParent != null &&
            searchResults.topLevelParent !== searchResults.analysisConfig &&
            searchResults.path?.length > 1
          ) {
            const relativePath = searchResults.path.slice(1)
            AnalysisConfigSearchService.setAnalysisConfigAtPath(
              searchResults.topLevelParent,
              relativePath,
              newAnalysisConfig
            )
          } else {
            activeAnalysisConfigs.splice(topIdx, 1, newAnalysisConfig)
          }
          notifyAnalysisConfigChangedStream$.next()
          nextTick(() => {
            selectedAnalysisConfig.value = activeAnalysisConfigs[topIdx]
          })
        } else if (searchResults.topLevelParent === searchResults.analysisConfig) {
          activeAnalysisConfigs[topIdx].configuration = { ...config }
          activeAnalysisConfigs[topIdx]._type = config._type
          notifyAnalysisConfigChangedStream$.next()
        }

        updateSharedModelOnAnalysisConfigChange()
      }
    }
  }

  const onJsonManagementConfigsApplied = () => {
    selectedAnalysisConfig.value = activeAnalysisConfigs?.length ? activeAnalysisConfigs[0] : null
    updateSharedModelOnAnalysisConfigChange()
  }

  const inputTabDisabled = computed(() => {
    if (
      selectedAnalysisConfig.value == null ||
      selectedAnalysisConfig.value._type === AnalysisConfigService.CUSTOM_ANALYSISCONFIG_TYPE
    )
      return true
    const activeSchema = AnalysisConfigService.tryGetAnalysisConfigSchemaByType(
      selectedAnalysisConfig.value._type as string,
      availableAnalysisConfigSchemas.value
    )
    return (
      activeSchema == null || activeSchema.type === AnalysisConfigService.CUSTOM_ANALYSISCONFIG_TYPE
    )
  })

  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeAnalysisConfigViewMode.value = { mode: 'edit' }
      } else if (activeAnalysisConfigViewMode.value.mode !== 'edit') {
        activeAnalysisConfigViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  watch(
    () => props.analysisFile,
    async (analysisFile?: AplosFile) => {
      selectedFile.value = analysisFile as AplosFile
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.analysisExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.analysisExecution?.id == null
      ) {
        stepStatusMessage = 'Anaylsis not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailableAnalysisConfigSchemas()

    // Apply the watch inside the onMounted so it runs after we have loaded schemas
    // When in singleconfig mode, require the input anaylsisConfig prop and populate activeAnalysisConfigs
    watch(
      () => [props.mode, props.analysisConfig],
      ([mode, inputAnaylsisConfig]) => {
        if (mode === 'singleconfig') {
          // single mode requires an analysis config prop
          if (!inputAnaylsisConfig || !props.analysisFile) {
            message.error(
              'Analysis Configuration and FileId required to add/edit a configuration!',
              defaultMessageDurationSeconds
            )
            // clear any previous state that is only relevant for multiconfig
            activeAnalysisConfigs.splice(0, activeAnalysisConfigs.length)
            selectedAnalysisConfig.value = null
          } else if (props.analysisConfig != null) {
            // TODO: confirm we can store type data with executed configurations
            if (props.analysisConfig.type == null) {
              const firstSchemaType = availableAnalysisConfigSchemas.value?.length
                ? availableAnalysisConfigSchemas.value[0]
                : null

              if (firstSchemaType == null) {
                message.error('Missing required Analysis Config CDN Schema content!')
              } else {
                props.analysisConfig.type = firstSchemaType.type
                emit('update:analysisConfig', props.analysisConfig)
              }
            }

            // Initialize configuration from schema so we can build UI
            const initializedAnalysisConfig = analysisConfigJsonParsingService.initializeEntity(
              props.analysisConfig,
              availableAnalysisConfigSchemas.value,
              1
            )
            activeAnalysisConfigs.splice(0, activeAnalysisConfigs.length, initializedAnalysisConfig)

            if (activeAnalysisConfigs.length > 0) {
              selectedAnalysisConfig.value = activeAnalysisConfigs[0]
            }
          }
        }
      },
      { immediate: true }
    )

    watch(
      activeAnalysisConfigs,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )

    updateWizardStepStatus()
  })

  onBeforeUnmount(() => {
    batchNotifyAnalysisConfigChangedSubscription?.unsubscribe()
  })

  // UI field groups helper
  const uiFieldGroups = computed(() => {
    return AnalysisConfigsUiService.getAnalysisConfigSchemaUiFieldGroups(
      selectedAnalysisConfig.value?.schema
    )
  })

  watch(
    [selectedAnalysisConfig, uiFieldGroups],
    ([cfg, groups]) => {
      if (cfg != null && groups != null && groups.length > 0) {
        if (activeAnalysisConfigTabKey.value === 'edit') return
        if (
          !groups.some(
            (g: AnalysisConfigSchemaUiFieldGroup) => g.name === activeAnalysisConfigTabKey.value
          )
        ) {
          activeAnalysisConfigTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  watch(selectedAnalysisConfig, (newCfg, oldCfg) => {
    if (newCfg && newCfg !== oldCfg) {
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeAnalysisConfigTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        activeAnalysisConfigViewMode.value = { mode: 'view' }
      } else {
        activeAnalysisConfigViewMode.value = { mode: 'edit' }
      }
    }
  })

  const goToCustomCalculationsDisabled = computed(() => {
    const shared = props.wizardModel?.sharedModel
    if (shared) {
      return !wizardNavigationService.getIsStepAvailable(
        AnalysisWizardStepType.CustomCalculations,
        shared
      )?.isAvailable
    }
    return true
  })

  const goToWizardStepByType = (stepType: AnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  // Add computed helper to detect multi vs single mode
  const isMulticonfig = computed(() => props.mode === 'multiconfig')

  const { isExtraLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeAnalysisConfigs: activeAnalysisConfigs,
    addAnalysisConfig,
    removeAnalysisConfig
  })
</script>

<template>
  <div
    :class="[
      'analysisconfig-container',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'analysisconfig-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter :style="isFitToBrowser ? { height: 'unset' } : {}">
      <!-- Left panel only shows in multiconfig mode -->
      <SplitterPanel v-if="isMulticonfig" :size="30" style="min-width: 350px">
        <div class="flex h-full flex-col border-r-2 shadow-sm">
          <div class="border-b p-4 shadow-sm">
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Source File</label>
              <div class="mb-2 flex items-center gap-2">
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  block
                  :disabled="isLoadingAplosFile || isLoadingAvailableAnalysisConfigs || showLoading"
                  @click="showFileManager = true"
                  :title="selectedFile ? 'Change selected file' : 'Select a file'"
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedFile?.name ?? 'Select Analysis' }}
                  </span>
                </Button>
                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="selectedFile && !isLoadingAplosFile"
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearFileSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Analysis Configs</h3>
                <p class="mt-1 text-sm">
                  {{ activeAnalysisConfigs.length }}
                  {{ activeAnalysisConfigs.length === 1 ? 'config' : 'configs' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Analysis Config' }}
                  </template>
                  <Button
                    :disabled="selectedFile == null"
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showAnalysisConfigSelection = true"
                    title="Add New"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagement = true"
                    title="View/Export JSON"
                    :disabled="
                      !selectedFile ||
                      activeAnalysisConfigs.length === 0 ||
                      isProcessingAnalysisConfigs
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="configs-list-container flex-1 p-4">
            <div class="configs-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <div
                v-if="activeAnalysisConfigs.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Analysis Configs Yet</h3>
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                    <li>• Analysis configs are created after selecting a file</li>
                    <li>• Use the + button to add a config type</li>
                    <li>• Configure each item in the right panel</li>
                  </ul>
                </div>
              </div>
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(cfg, index) in activeAnalysisConfigs"
                  :key="cfg.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedAnalysisConfig?.id === cfg.id,
                      'opacity-50': draggedAnalysisConfig?.id === cfg.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'config-moved-highlight': highlightedAnalysisConfigId === cfg.id,
                      'config-deleting': deletingAnalysisConfigId === cfg.id
                    }
                  ]"
                  :bodyStyle="{ padding: '18px' }"
                  draggable="true"
                  @click="selectAnalysisConfig(cfg)"
                  @dragstart="handleDragStart($event, cfg)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab text-gray-400 active:cursor-grabbing" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ cfg.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ cfg.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Button
                          type="text"
                          size="small"
                          :disabled="activeAnalysisConfigs.findIndex((p) => p.id === cfg.id) === 0"
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveConfigUp(cfg)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeAnalysisConfigs.findIndex((p) => p.id === cfg.id) ===
                            activeAnalysisConfigs.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveConfigDown(cfg)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removeAnalysisConfig(cfg)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>

      <!-- Right panel takes full width when single mode -->
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedAnalysisConfig
                    ? `Configure: ${selectedAnalysisConfig.display_name}`
                    : 'Analysis'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedFile != null
                    ? 'Configure the selected item below'
                    : 'Select an analysis to begin configuring'
                }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    selectedFile == null || !activeAnalysisConfigs?.length
                      ? 'Calculate PK Parameters to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Download
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View Data -->
              <a-tooltip>
                <template #title>
                  {{
                    selectedFile == null
                      ? 'Select a dataset to view'
                      : !isProcessingAnalysisConfigs
                        ? 'View Data'
                        : 'Calculating PK parameters'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="!selectedFile"
                  @click="viewData(selectedFile)"
                >
                  <span class="ant-button-flex-content">
                    <FileSearchOutlined
                      class="size-4"
                      stroke-width="1.5"
                      :class="{
                        color: !selectedFile ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="!selectedFile"
                  @click="viewData(selectedFile)"
                >
                  <span class="ant-button-flex-content">
                    <FileSearchOutlined
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !selectedFile ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'View Data' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Calculate PK Parameters-->
              <a-tooltip :open="(selectedFile == null ||
                    !activeAnalysisConfigs?.length ||
                    isProcessingAnalysisConfigs) ? false : undefined">
                <template #title>
                  {{
                    selectedFile == null || !activeAnalysisConfigs?.length
                      ? 'Select a file and add analysis configs to Calculate PK Parameters'
                      : !isProcessingAnalysisConfigs
                        ? 'Calculate PK Parameters'
                        : 'Calculating PK parameters'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedFile == null ||
                    !activeAnalysisConfigs?.length ||
                    isProcessingAnalysisConfigs
                  "
                  @click="onSubmitAnalysisConfigsForGenerationClicked"
                >
                  <Loader2 v-if="isProcessingAnalysisConfigs" class="size-4 animate-spin" />
                  <CloudCog v-if="!isProcessingAnalysisConfigs" class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedFile == null ||
                    !activeAnalysisConfigs?.length ||
                    isProcessingAnalysisConfigs
                  "
                  @click="onSubmitAnalysisConfigsForGenerationClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingAnalysisConfigs" class="mr-2 size-4 animate-spin" />
                    <CloudCog v-else class="h-4 w-4" />
                    {{ 'Calculate PK Parameters' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View/Export JSON-->
              <a-tooltip placement="left">
                <template #title>
                  {{ 'View/Export JSON' }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedFile && activeAnalysisConfigs.length === 0) ||
                    isProcessingAnalysisConfigs
                  "
                >
                  <CodeOutlined class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  :type="selectedFile && activeAnalysisConfigs.length !== 0 ? 'button' : 'default'"
                  :class="{ 'small-button': selectedFile && activeAnalysisConfigs.length !== 0 }"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedFile && activeAnalysisConfigs.length === 0) ||
                    isProcessingAnalysisConfigs
                  "
                >
                  <span class="ant-button-flex-content">
                    <CodeOutlined class="h-4 w-4" />
                    {{ 'View/Export JSON' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Go to Custom Calculations -->
              <a-tooltip placement="left" v-if="props.wizardModel != null">
                <template #title>Go to Custom Calculations</template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="goToCustomCalculationsDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.CustomCalculations)
                    }
                  "
                >
                  <Calculator class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="goToCustomCalculationsDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.CustomCalculations)
                    }
                  "
                >
                  <span class="ant-button-flex-content">
                    {{ 'Custom Calculations' }}
                    <ChevronRight class="h-4 w-4" />
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>

          <div
            v-if="(isLoadingAvailableAnalysisConfigs || showLoading) && !wizardModel"
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading configurations...</p>
            </div>
          </div>

          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="
                mode === 'multiconfig' && selectedFile == null && !hasErrorLoadingAnalysisConfigs
              "
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select a file to begin configuring analysis
            </p>

            <p
              v-else-if="
                mode === 'singleconfig' &&
                (props.analysisFile == null || props.analysisConfig == null)
              "
              class="mt-8 text-center text-sm text-red-600"
            >
              An analysis configuration and file are required to add/edit a configuration.
            </p>

            <div v-else>
              <div
                v-if="
                  selectedAnalysisConfig != null && uiFieldGroups != null && uiFieldGroups?.length
                "
                class="min-h-0 flex-1"
              >
                <a-tabs
                  :key="selectedAnalysisConfig?.id"
                  v-model:activeKey="activeAnalysisConfigTabKey"
                  @change="onConfigViewModeTabChange"
                  :destroyInactiveTabPane="true"
                >
                  <a-tab-pane
                    v-for="(fieldGroup, index) in uiFieldGroups"
                    :key="fieldGroup.name"
                    :tab="fieldGroup.name"
                  >
                    <AnalysisConfigConfiguration
                      :configuration-level="1"
                      :analysisConfig="selectedAnalysisConfig"
                      :root-analysis-config="selectedAnalysisConfig"
                      :ui-field-group="fieldGroup"
                      :available-columns="availableColumns"
                      @configuration-updated="handleConfigurationUpdate"
                      :config-schemas="availableAnalysisConfigSchemas"
                      :show-description="index === 0"
                      :is-condensed-layout="true"
                    />
                  </a-tab-pane>

                  <a-tab-pane
                    v-if="showJsonTab"
                    key="edit"
                    tab="JSON"
                    :disabled="
                      AnalysisConfigTypeGuardService.isPayloadTemplateAnalysisConfigSchema(
                        selectedAnalysisConfig?.schema
                      )
                    "
                  >
                    <div class="mt-[20px]">
                      <JsonAnalysisConfigConfiguration
                        v-if="activeAnalysisConfigEditConfiguration != null"
                        :key="selectedAnalysisConfig?.id"
                        :analysisConfigSchemas="availableAnalysisConfigSchemas"
                        :available-columns="availableColumns"
                        v-model="activeAnalysisConfigEditConfiguration"
                        :editor-schema="
                          AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForAnalysisConfig()
                        "
                        :place-holder="'Configure JSON'"
                        :configuration-level="1"
                        @update:modelValue="handleJsonEditorConfigurationChange"
                        :allow-type-change="false"
                      />
                    </div>
                  </a-tab-pane>
                </a-tabs>
              </div>

              <a-tabs
                v-else-if="selectedAnalysisConfig != null"
                @change="onConfigViewModeTabChange"
                :key="selectedAnalysisConfig?.id"
                v-model:activeKey="activeAnalysisConfigViewMode.mode"
                :destroyInactiveTabPane="false"
              >
                <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                  <AnalysisConfigConfiguration
                    :configuration-level="1"
                    :analysisConfig="selectedAnalysisConfig"
                    :root-analysis-config="selectedAnalysisConfig"
                    :available-columns="availableColumns"
                    @configuration-updated="handleConfigurationUpdate"
                    :config-schemas="availableAnalysisConfigSchemas"
                    :show-description="true"
                    :is-condensed-layout="true"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    AnalysisConfigTypeGuardService.isPayloadTemplateAnalysisConfigSchema(
                      selectedAnalysisConfig?.schema
                    )
                  "
                >
                  <div class="mt-[20px]">
                    <JsonAnalysisConfigConfiguration
                      v-if="activeAnalysisConfigEditConfiguration != null"
                      :key="selectedAnalysisConfig?.id"
                      :analysisConfigSchemas="availableAnalysisConfigSchemas"
                      :available-columns="availableColumns"
                      v-model="activeAnalysisConfigEditConfiguration"
                      :editor-schema="
                        AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForAnalysisConfig()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <AnalysisConfigSelectionModal
      v-model:visible="showAnalysisConfigSelection"
      :analysisConfigSchemas="availableAnalysisConfigSchemas"
      @analysis-config-selected="addAnalysisConfig"
    />

    <Modal
      v-model:open="showFileManager"
      title="Select a File"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <FileManager
          @file-selected="handleFileSelected"
          :show-header="false"
          :selectable="false"
          :show-actions="false"
          title="Select a file for analysis"
          description="Click on a file to select it"
        />
      </div>
    </Modal>

    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Analysis Config JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <AnalysisConfigJsonManagement
        v-if="selectedFile != null && availableAnalysisConfigSchemas != null"
        :key="selectedAnalysisConfig?.id"
        :analysis-config-schemas="availableAnalysisConfigSchemas"
        :file-id="selectedFile?.id!"
        :analysis-configs="activeAnalysisConfigs"
        :visibility-changed="showJsonManagement"
        @configs-applied="onJsonManagementConfigsApplied"
      />
    </Drawer>
  </div>
</template>

<style scoped>
  .analysisconfig-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Allow component to fill available space via flex but don't force parent to grow */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .analysisconfig-fill {
    flex: 1 1 0;
  }

  .file-select-button {
    text-align: center;
    flex: 1 1 0;
    min-width: 0;
  }
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* left list container becomes a flex column and will handle scrolling itself */
  .configs-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  /* full-height behavior only when fit-to-browser */
  .fit-to-browser .configs-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }

  .configs-scroll-area {
    min-height: 0;
    overflow: visible;
  }
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
